import type { ChartKind, ChartPoint, ChartProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import {
  CheckboxField,
  ColorField,
  NumberField,
  SelectField,
  TextField,
} from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const KIND = ["bar-vertical", "bar-horizontal"] as const;

function newId(): string {
  return `c-${Math.random().toString(36).slice(2, 8)}`;
}

export function ChartInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as ChartProps;
  const points = p.points ?? [];

  const setPoints = (next: ChartPoint[]) => update(widget.id, { points: next });
  const addPoint = () => setPoints([...points, { id: newId(), label: "New", value: 0 }]);
  const updatePoint = (id: string, patch: Partial<ChartPoint>) =>
    setPoints(points.map((pt) => (pt.id === id ? { ...pt, ...patch } : pt)));
  const removePoint = (id: string) => setPoints(points.filter((pt) => pt.id !== id));
  const movePoint = (id: string, dir: "up" | "down") => {
    const idx = points.findIndex((pt) => pt.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= points.length) return;
    const next = points.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setPoints(next);
  };

  return (
    <>
      <SelectField
        label="Kind"
        value={p.kind}
        options={KIND}
        onChange={(v) => update(widget.id, { kind: v as ChartKind })}
      />
      <NumberField
        label="Height (px)"
        value={p.height ?? 260}
        min={80}
        max={600}
        onChange={(v) => update(widget.id, { height: v })}
      />
      <CheckboxField
        label="Show values"
        value={p.showValues ?? true}
        onChange={(v) => update(widget.id, { showValues: v })}
      />
      <ColorField
        label="Accent"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />
      <ColorField
        label="Grid / track"
        value={p.gridColor ?? "#e5e7eb"}
        onChange={(v) => update(widget.id, { gridColor: v })}
      />
      <ColorField
        label="Labels"
        value={p.labelColor ?? "#6b7280"}
        onChange={(v) => update(widget.id, { labelColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Data points ({points.length})
      </div>

      {points.map((pt, i) => (
        <div
          key={pt.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => movePoint(pt.id, "up")}
              disabled={i === 0}
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => movePoint(pt.id, "down")}
              disabled={i === points.length - 1}
              style={{
                padding: "2px 6px",
                opacity: i === points.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removePoint(pt.id)}
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <div className="emke-inspector-grid-2" style={{ padding: "0" }}>
            <TextField
              label={`Label ${i + 1}`}
              value={pt.label}
              onChange={(v) => updatePoint(pt.id, { label: v })}
            />
            <NumberField
              label="Value"
              value={pt.value}
              min={0}
              max={100000}
              onChange={(v) => updatePoint(pt.id, { value: v })}
            />
          </div>
          <ColorField
            label="Colour (override)"
            value={pt.color ?? ""}
            onChange={(v) => updatePoint(pt.id, { color: v || undefined })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addPoint}
          style={{ width: "100%" }}
        >
          + Add data point
        </button>
      </div>
    </>
  );
}
